<template>
	<CardAbstract :app="app" :card="card">

		<div class="card-base">

			<!-- Title -->
			<div v-if="$slots.title" class="card-base__title">
				<div 
					class="card-base__title-button left" 
					:class="{ 'fullscreen': isFullscreen }">
					<v-tooltip
						location="bottom"
						v-model="showSizeTooltip"
						:disabled="disableSizeTooltip"
						:text="isFullscreen ? 'minimize cards' : 'maximize cards'">
						<template v-slot:activator="{ props }">
							<v-btn 
								class="hide-on-mobile"
								variant="text" 
								elevation="0" 
								v-bind="props"
								@click="handleSize"
								@mouseleave="disableSizeTooltip = false">
								<v-icon v-if="isFullscreen" icon="fa-solid fa-down-left-and-up-right-to-center" />
								<v-icon v-else icon="fa-solid fa-up-right-and-down-left-from-center" />
							</v-btn>
						</template>
					</v-tooltip>
				</div>
				<div class="card-base__title-text">
					<slot name="title" />
				</div>
				<div 
					class="card-base__title-button right" 
					:class="{ 'fullscreen': isFullscreen }">
					<v-tooltip 
						location="bottom"
						text="close this card"
						v-model="showCloseTooltip"
						:disabled="disableCloseTooltip">
						<template v-slot:activator="{ props }">
							<v-btn 
								v-if="!isFirstCard" 
								variant="plain" 
								elevation="0" 
								v-bind="props" 
								@click="handleRemove" 
								@mouseleave="disableCloseTooltip = false">
								<v-icon icon="fa-solid fa-xmark" />
							</v-btn>
						</template>
					</v-tooltip>
				</div>
			</div>

			<!-- Content -->
			<div class="card-base__content">

				<div 
					class="card-base__content-container" 
					:class="{ 'filter-blur': hasOverlay }">

					<div v-if="$slots.status" class="status">
						<slot name="status"></slot>
					</div>

					<div class="layout">
						<div class="content">
							<slot name="content"></slot>
						</div>

						<div v-if="$slots.side" class="side hide-on-mobile">
							<slot name="side"></slot>
						</div>
					</div>
				</div>

				<TransitionFade>
					<div v-if="hasOverlay" class="card-base__content-overlay">

						<v-progress-circular 
							indeterminate 
							v-if="localLoading && !localError" 
							class="progress" />

						<v-icon 
							v-if="localError" 
							icon="fa-solid fa-exclamation-triangle" 
							class="error">
						</v-icon>

						<div v-if="localMessage" class="message">
							{{ localMessage }}
						</div>

					</div>
				</TransitionFade>

			</div>

			<!-- Actions -->
			<div
				class="card-base__actions-shell"
				:class="{ 'is-visible': actionable }"
				:aria-hidden="!actionable">
			<div class="card-base__actions">
				<div class="card-base__actions-outer">

					<v-window v-if="!localLoading && !localError" direction="vertical" v-model="currentWindow">

						<v-window-item class="card-base__actions-window" :value="0">
							<div class="color-primary" v-html="guidance"></div>
						</v-window-item>

						<v-window-item class="card-base__actions-window" :value="1">
							<div v-if="outdated" class="card-base__actions-inner">
								<span class="color-error">
									Action no longer available.
								</span>
								<v-btn v-if="hasActions" elevation="0" @click="handleReset">
									select new action
								</v-btn>
							</div>
							<div v-else class="card-base__actions-inner">
								<div v-if="hasActions" class="card-base__actions-inner-question">What's next?</div>
								<div class="card-base__actions-inner-list">
									<v-btn 
										v-for="(action, index) in actions" 
										size="large" 
										variant="tonal"
										class="button"
										:class="{ 'selected': selectedAction === action?.id }" 
										@click="handleAction(action)">
										{{ action.label }}
									</v-btn>
								</div>
							</div>

						</v-window-item>

					</v-window>

				</div>
			</div>
			</div>
		</div>

	</CardAbstract>
</template>

<script>
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { TransitionFade } from '@generio-ai/library-legacy/components/transitions'
import CardAbstract from '@/components/flow/cards/CardAbstract.vue'
import { serializeError } from '@/utils/error.js'

export default {
	name: 'CardBase',
	components: {
		TransitionFade,
		CardAbstract
	},
	props: {
		app: {
            type: String,
            required: true
        },
		card: {
			type: Object,
			required: true
		},
		actionable: {
			type: Boolean,
			default: true
		},
		actions: {
			type: Array,
			default: () => []
		},
		guidance: {
			type: String,
			default: null
		},
		loading: {
			type: Boolean,
			default: false
		},
		error: {
			type: Boolean,
			default: false
		},
		message: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			mounted: false, 

			localLoading: false,
			localError: false,
			localMessage: '',

			outdated: false,

			showSizeTooltip: false,
			disableSizeTooltip: false,
			showCloseTooltip: false,
			disableCloseTooltip: false
		}
	},
	computed: {
		...mapGetters('flows', [
			'isActiveByIndex',
			'isFullscreen',
			'hasMultiple'
		]),

		isActive() {
			return this.mounted && this.isActiveCard
		},
		isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
		isFirstCard() {
			return this.index === 0
		},

		hasMultipleCards() {
			return this.hasMultiple(this.app)
		},

		hasOverlay() {
			return this.localLoading || this.localError
		},
		hasActions() {
			return this.actions != null && this.actions.length > 0
		},
		hasGuidance() {
			return this.guidance !== null && this.guidance !== undefined && this.guidance !== ''
		},

		currentWindow: {
			get() {
				return this.hasGuidance ? 0 : 1
			},
			set(value) {}
		},

		index() {
			return this.card.index
		},
		selectedAction() {
			return this.card.action
		},
	},
	watch: {
		loading(newValue, oldValue) {
			if (newValue === oldValue) return
			this.localLoading = newValue
		},
		error(newValue, oldValue) {
			if (newValue === oldValue) return
			this.localError = newValue
		},
		message(newValue, oldValue) {
			if (newValue === oldValue) return
			this.localMessage = newValue
		},
		localLoading(newValue, oldValue) {
			if (newValue === oldValue) return
			this.$emit('update:loading', newValue)
		},
		actions(newValue, oldValue) {
			if (newValue === oldValue) return
			this.scan()
		}
	},
	emits: [
		'update:loading',
		'update:error',
		'update:message',
		'action',
		'init'
	],
	methods: {
		...mapActions('analytics', [
            'logError'
        ]),
		...mapActions('flows', [
			'removeCard',
			'toggleFullscreen'
		]),
		init() 
		{
			// get the transaction
			const transaction = this.card?.transaction

			// if transaction exists
			if (transaction) 
			{
				if (transaction.isRunable()) 
				{
					// activate loading 
					this.localLoading = true
	
					// register callbacks
					transaction.onProgress((message, stage, assets) => {
						this.card.stage = stage
						this.card.assets = assets
						this.localMessage = message
					})
					transaction.onFinish((assets, stage) => {
						this.card.stage = stage
						this.card.assets = assets
						this.localLoading = false
						this.$emit('init', assets, true)
					})
					transaction.onError((error) => {
						this.card.stage = null
						this.localError = true
						this.localMessage = 'something went wrong'

						this.logError({
							name: `Card Init Failed`,
							description: error?.message || 'no details available',
							additional: serializeError(error)
						})
					})
	
					// execute transaction
					transaction.run()
				}
				else
				{
					this.$emit('init', this.card.assets, false)
				}
			}
	
			this.scan()
		},
		scan() {
			if (this.selectedAction !== null && !this.actions.find(a => a.id === this.selectedAction)) {
				this.outdated = true
			} else {
				this.outdated = false
			}
		},
		reset() {
			this.card.action = null
			this.outdated = false
		},

		handleSize() {
			// immediately close and suppress the tooltip while clicking
			this.showSizeTooltip = false
			this.disableSizeTooltip = true
			this.toggleFullscreen()
		},
		handleRemove() {
			this.removeCard({ app: this.app, index: this.card.index })
		},
		handleReset() {
			this.card.action = null
			this.outdated = false
			this.removeCard({ app: this.app, index: this.card.index + 1 })
		},
		handleAction(action) {
			this.card.action = action.id
			this.$emit('action', action)
		},
		handleKeyboardShortcuts(event) 
		{
			// only if the card is active
			if (!this.isActiveCard) return

			// only if actions are visible
			if (this.hasGuidance) return

			// Ensure there are actions to trigger
			if (!this.actions || this.actions.length === 0) return

			// Get the pressed key
			const key = event.key

			// Check if the key is a single digit (1-9)
			if (/^[1-9]$/.test(key)) {

				// Convert to 0-indexed position
				const index = parseInt(key, 10) - 1

				// If an action exists at that index, trigger it
				if (index >= 0 && index < this.actions.length) {

					event.preventDefault()
					this.handleAction(this.actions[index])
				}
			}
        }
	},
	mounted() {
		this.mounted = true
		this.init()
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>

<style>
@media (max-width: 600px) {
	.card-base__actions .v-btn__content {
		white-space: normal !important;
	}
}
</style>

<style scoped>
.card-base {
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	box-sizing: border-box;
}

.card-base__title {
	position: relative;
	width: 100%;
	min-height: 65px;
	background-color: rgb(var(--v-theme-darkbase));
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 1.7rem;
	border-bottom: 1px solid rgb(var(--v-theme-lightbase));
	user-select: none;
}

.card-base__title-button {
	min-width: 74px;
}

.card-base__title-button.fullscreen {
	min-width: 120px;
	display: flex;
	align-items: center;
	gap: 10px;
}

.card-base__title .v-btn {
	min-height: 65px;
	min-width: 74px;
	border-radius: 0px !important;
}

.card-base__title-button.left {
	justify-content: flex-start;
}

.card-base__title-button.left .v-btn .v-icon {
	transform: scaleX(-1);
	font-size: 1.4rem;
	color: rgb(var(--v-theme-tertiary));
}

.card-base__title-button.left .v-btn:hover {
	background-color: rgba(var(--v-theme-lightbase), 0.3) !important;
}

.card-base__title-button.right {
	justify-content: flex-end;
}

.card-base__title-button.right .v-btn .v-icon {
	font-size: 1.5rem;
	color: rgb(var(--v-theme-secondary));
}

.card-base__title-button.right .v-btn:hover {
	background-color: rgba(var(--v-theme-error), 0.3) !important;
}

.card-base__title-text {
	flex-grow: 1;
	display: flex;
	justify-content: center;
	align-items: center;
}

.card-base__content {
	position: relative;
	width: 100%;
	flex-grow: 1;
	min-height: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	overflow: hidden;
}

.card-base__content-container {
	width: 100%;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	min-width: 0;
}

.card-base__content-container .status {
	min-height: 3rem;
	width: 100%;
	flex-shrink: 1;
}

.card-base__content-container .layout {
	width: 100%;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	flex-grow: 1;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
}

.card-base__content-container .layout .content {
	height: 100%;
	max-height: 100%;
	min-height: 0;
	width: 100%;
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}

.card-base__content-container .layout .side {
	height: 100%;
	max-height: 100%;
	min-width: 300px;
	flex-shrink: 1;
	padding-left: 1rem;
	padding-right: 1rem;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: rgba(var(--v-theme-darkerbase), 0.5);
	box-shadow: inset 10px 0 10px -10px rgba(var(--v-theme-shadow1), 0.5);
}

.card-base__content-overlay {
	position: absolute;
	width: 100%;
	height: 100%;
	left: 0px;
	top: 0px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-shadow: 0 0 8px rgb(var(--v-theme-shadow1));
	background-color: rgba(var(--v-theme-darkground), 0.2);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
	z-index: 498;
	user-select: none;
	pointer-events: all;
	overflow: hidden;
}

.card-base__content-overlay .progress {
	transform: scale(2);
	margin-bottom: 2rem;
}

.card-base__content-overlay .error {
	color: rgb(var(--v-theme-error));
	font-size: 6rem !important;
}

.card-base__content-overlay .message {
	font-size: 1.8rem;
}

.card-base__actions-shell {
	--card-actions-height: 65px;
	flex: 0 0 auto;
	height: 0;
	overflow: hidden;
	visibility: hidden;
	transition: height 0.3s ease;
}

.card-base__actions-shell.is-visible {
	height: var(--card-actions-height);
	visibility: visible;
}

.card-base__actions {
	width: 100%;
	height: 100%;
	min-height: 0;
	font-size: 1.6rem;
	opacity: 0;
	transform: translateY(10px);
	transition: opacity 0.3s ease, transform 0.3s ease;
}

.card-base__actions-shell.is-visible .card-base__actions {
	opacity: 1;
	transform: translateY(0);
}

.card-base__actions-window {
	display: block;
	height: 100%;
}

.card-base__actions-outer {
	width: 100%;
	height: 100%;
	border-top: 1px solid rgb(var(--v-theme-lightbase));
	background-color: rgb(var(--v-theme-darkbase));
}

.card-base__actions-outer div {
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	white-space: nowrap;
}

.card-base__actions-inner {
	gap: 10px;
}

.card-base__actions-inner-list {
	gap: 5px;
}

.card-base__actions .button {
	background: linear-gradient(0deg, rgb(var(--v-theme-background)), rgb(var(--v-theme-lightbase)));
}

.card-base__actions .button:hover {
	color: rgb(var(--v-theme-white));
	background: linear-gradient(0deg, rgb(var(--v-theme-color1)), rgb(var(--v-theme-color1)));
}

.card-base__actions .button.selected {
	color: rgb(var(--v-theme-white));
	background: linear-gradient(0deg, rgb(var(--v-theme-color2)), rgb(var(--v-theme-color1)));
}

@media (max-width: 1600px) {
	.card-base__actions-shell {
		--card-actions-height: 60px;
	}

	.card-base__title {
		min-height: 60px;
	}

	.card-base__actions {
		font-size: 1.4rem;
	}

	.card-base__actions .v-btn {
		padding-left: 12px !important;
		padding-right: 12px !important;
		font-size: 0.9rem;
	}
}

@media (max-width: 1000px) {
	.card-base__title-button {
		min-width: 60px;
	}

	.card-base__title-button.fullscreen {
		min-width: 120px;
	}

	.card-base__title-button .v-btn {
		max-height: 40px;
		max-width: 40px;
	}
}

@media (max-width: 800px) {
	.card-base__title {
		font-size: 1.3rem;
	}

	.card-base__title-text {
		padding-left: 10px;
	}

	.card-base__title-button {
		min-width: 45px;
	}

	.card-base__title-button.fullscreen {
		min-width: 45px;
	}

	.card-base__title-button .v-btn {
		max-height: 35px;
		max-width: 35px;
	}

	.card-base__actions {
		font-size: 1.2rem;
	}

	.card-base__actions .v-btn {
		font-size: 0.9rem;
	}
}

@media (max-width: 600px) {
	.card-base__actions-shell {
		--card-actions-height: 55px;
	}

	.card-base__title {
		min-height: 55px;
		max-height: 55px;
		font-size: 1.3rem;
	}

	.card-base__title-button.right .v-btn {
		min-height: 55px;
		max-height: 55px;
	}
	
	.card-base__title-button.left .v-btn {
		min-height: 55px;
		max-height: 55px;
	}

	.card-base__actions {
		font-size: 1.1rem;
	}

	.card-base__actions .v-btn {
		min-width: 150px;
		padding-left: 8px !important;
		padding-right: 8px !important;
	}

	.card-base__actions-window {
		width: 100%;
		max-width: 100vw;
	}

	.card-base__actions-inner {
		width: 100%;
		max-width: 100vw;
		overflow-x: auto;
	}

	.card-base__actions-inner-list {
		display: block !important;
		width: 100%;
	}

	.card-base__actions-inner-question {
		display: none !important;
	}

	.card-base__actions-inner .v-btn {
		margin: 5px;
		margin-right: 0px;
	}

	.card-base__actions-inner .v-btn:last-child {
		margin-right: 5px !important;
	}
}

@media (max-width: 450px) {
	.card-base__title-text {
		padding-left: 20px;
		justify-content: flex-start;
		font-size: 1.0rem;
	}

	.card-base__title-button.left {
		display: none;
	}

	.card-base__title-button.right {
		height: 100%;
	}

	.card-base__title-button.right .v-btn {
		min-height: 55px;
		max-height: 55px;
		max-width: unset;
		height: 100%;
	}

	.card-base__actions .v-btn {
		font-size: 0.9rem;
	}

	.card-base__actions-window {
		font-size: 0.9rem;
	}
}
</style>
